import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {getProblemStatistics, getWorkOrderStatistics} from '../../actions';
import IgnoreProblemModal from './IgnoreProblemModal';

class ProblemDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      statistics: [],
      summary: {},
      groupBy: 'problem_type',
      loading: false,
      // Drill-down state
      currentLevel: 'summary', // 'summary' | 'workOrders' | 'actions'
      breadcrumbs: [], // Array of {label, groupBy, value, level}
      workOrders: [],
      workOrdersLoading: false,
      selectedRowIndex: null, // For keyboard navigation
      // Filter state - CRITICAL: filters must persist across grouping changes
      activeFilters: {}, // e.g., {office: 'Burbank', problemType: 'FAILED'}
      appliedFilterLabel: null, // For display: "Office = Burbank"
      filteredActionIDs: null, // Array of action IDs to filter actions table
      showMuteModal: false,
      muteModalData: null // {actionIDs: [], problemCount: number, workOrder: string}
    };
    this.tableRef = React.createRef();
  }

  componentDidMount() {
    this.loadStatistics();
    // Add keyboard event listeners
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    // Reload when show_problematic_only changes or date filter changes
    const {showProblematicOnly, createdOnFilter} = this.props;
    if (prevProps.showProblematicOnly !== showProblematicOnly) {
      if (showProblematicOnly) {
        this.loadStatistics();
      }
    } else if (showProblematicOnly && 
               JSON.stringify(prevProps.createdOnFilter) !== JSON.stringify(createdOnFilter)) {
      // Date filter changed, reload statistics
      this.loadStatistics();
    }
    
    // Ensure filteredActionIDs are cleared when at summary level
    const {currentLevel, filteredActionIDs} = this.state;
    if (currentLevel === 'summary' && filteredActionIDs !== null) {
      const {onFilteredActionIDsChange} = this.props;
      if (onFilteredActionIDsChange) {
        onFilteredActionIDsChange(null);
      }
      this.setState({filteredActionIDs: null});
    }
  }

  loadStatistics = (groupBy = null, filters = null, preserveSummary = false) => {
    const {dispatch, createdOnFilter} = this.props;
    const group = groupBy || this.state.groupBy;
    const activeFilters = filters || this.state.activeFilters;
    const currentSummary = this.state.summary; // Preserve current summary if needed
    
    this.setState({loading: true});
    
    // Build query params with date filter
    const params = {group_by: group};
    if (createdOnFilter) {
      if (createdOnFilter.start) {
        params.created_on_start = createdOnFilter.start instanceof Date 
          ? createdOnFilter.start.toISOString() 
          : createdOnFilter.start;
      }
      if (createdOnFilter.end) {
        params.created_on_end = createdOnFilter.end instanceof Date 
          ? createdOnFilter.end.toISOString() 
          : createdOnFilter.end;
      }
    }
    
    const promise = dispatch(getProblemStatistics(group, params));
    
    if (promise && promise.then) {
      promise.then(
        (response) => {
          if (response && response.statistics) {
            // Apply client-side filtering if filters are active
            // BUT: Keep the original summary - it should always reflect ALL statistics, not filtered ones
            let filteredStats = response.statistics;
            if (Object.keys(activeFilters).length > 0) {
              filteredStats = this.applyFiltersToStatistics(response.statistics, activeFilters);
            }
            
            // If preserveSummary is true (when switching groups), keep existing summary
            // Otherwise, use the new summary from backend (when filters change or initial load)
            const summaryToUse = preserveSummary && currentSummary && Object.keys(currentSummary).length > 0
              ? currentSummary
              : (response.summary || {});
            
            this.setState({
              statistics: filteredStats,
              summary: summaryToUse, // Preserve existing summary when switching groups
              groupBy: group,
              loading: false
            });
          } else {
            this.setState({loading: false});
          }
        }
      ).catch(() => {
        this.setState({loading: false});
      });
    } else {
      // If dispatch doesn't return a promise, handle synchronously
      this.setState({loading: false});
    }
  }

  applyFiltersToStatistics = (statistics, filters) => {
    // Filter statistics based on active filters
    return statistics.filter(stat => {
      if (filters.office && stat.office !== filters.office) return false;
      if (filters.problemType && stat.problemLabel !== filters.problemType) return false;
      if (filters.rootAction && stat.rootActionType !== filters.rootAction) return false;
      return true;
    });
  }

  handleGroupByChange = (newGroupBy) => {
    // When changing grouping, preserve active filters AND summary
    const {activeFilters, summary} = this.state;
    
    // Clear filtered action IDs when going back to summary level
    const {onFilteredActionIDsChange} = this.props;
    if (onFilteredActionIDsChange) {
      onFilteredActionIDsChange(null);
    }
    
    // Preserve the summary when switching groups - it represents totals across all data
    // and should remain consistent regardless of grouping
    this.setState({
      groupBy: newGroupBy,
      currentLevel: 'summary',
      breadcrumbs: [],
      selectedRowIndex: null,
      filteredActionIDs: null, // Clear filtered action IDs at summary level
      summary: summary // Preserve existing summary
      // Keep activeFilters - don't clear them!
    });
    // Load new statistics but preserve summary in state update
    //this.loadStatistics(newGroupBy, activeFilters, summary);
    this.loadStatistics(newGroupBy, activeFilters, true);
  }

  // Click behavior A: Row click → Apply filter and show actions
  handleRowClick = (e, group) => {
    // Don't trigger if clicking on count or WO link
    if (e.target.closest('.count-cell') || e.target.closest('.wo-count-link')) {
      return;
    }
    
    const {groupBy} = this.state;
    const groupValue = this.formatGroupValue(groupBy, group.key);
    
    // Apply filter based on grouping dimension
    const newFilter = {};
    const filterLabel = this.getFilterLabel(groupBy, groupValue);
    
    if (groupBy === 'office') {
      newFilter.office = group.key;
    } else if (groupBy === 'problem_type') {
      newFilter.problemType = group.key;
    } else if (groupBy === 'root_action') {
      newFilter.rootAction = group.key;
    } else if (groupBy === 'work_order') {
      newFilter.workOrder = group.key;
    }
    
    // Extract action IDs from the group to filter actions
    const actionIDs = group.actionIDs && group.actionIDs.length > 0 
      ? group.actionIDs.map(id => parseInt(id, 10)).filter(id => !isNaN(id) && id > 0)
      : [];
    
    // Update state with filter and action IDs
    this.setState({
      activeFilters: newFilter,
      appliedFilterLabel: filterLabel,
      currentLevel: 'actions',
      breadcrumbs: [{
        label: filterLabel,
        groupBy,
        value: group.key,
        level: 'summary'
      }],
      filteredActionIDs: actionIDs
    });
    
    // Apply filter to actions table via parent component
    this.applyActionFilters(newFilter, actionIDs);
  }
  
  getFilterLabel = (groupBy, value) => {
    const labels = {
      'office': `Office = ${value}`,
      'problem_type': `Problem Type = ${value}`,
      'root_action': `Root Action = ${value}`,
      'work_order': `Work Order = ${value}`
    };
    return labels[groupBy] || `${groupBy} = ${value}`;
  }
  
  applyActionFilters = (filters, actionIDs = []) => {
    const {onFilterChange, onFilteredActionIDsChange} = this.props;
    
    // Notify parent component of filtered action IDs so it can filter the actions table
    if (onFilteredActionIDsChange) {
      onFilteredActionIDsChange(actionIDs.length > 0 ? actionIDs : null);
    }
  }

  // Click behavior B: "X WOs" link click → Switch to Work Order grouping + preserve current filter
  handleWOCountClick = (e, group, groupBy) => {
    e.stopPropagation();
    
    // CRITICAL: Preserve current filters when switching to Work Order grouping
    // Don't change groupBy here - we want to keep the current grouping context
    // (e.g., 'office') so the work orders are filtered by that context
    
    // Pass the actual group data, not a dummy object
    // When clicking "X WOs" from office view, we want to show all WOs for that office
    // So we pass the group with the office filter preserved
    // handleShowWorkOrders will set currentLevel to 'workOrders'
    this.handleShowWorkOrders(group, groupBy);
  }

  // Click behavior C: Count click → Show actions directly
  handleCountClick = (e, group, groupBy) => {
    e.stopPropagation();
    // Same as handleRowClick but triggered from count cell
    this.handleRowClick(e, group);
  }
  
  handleShowActions = (group, groupBy) => {
    // This method is called to show actions for a specific group
    // Extract action IDs and apply filters
    const groupValue = this.formatGroupValue(groupBy, group.key);
    const filterLabel = this.getFilterLabel(groupBy, groupValue);
    
    const newFilter = {};
    if (groupBy === 'office') {
      newFilter.office = group.key;
    } else if (groupBy === 'problem_type') {
      newFilter.problemType = group.key;
    } else if (groupBy === 'root_action') {
      newFilter.rootAction = group.key;
    } else if (groupBy === 'work_order') {
      newFilter.workOrder = group.key;
    }
    
    const actionIDs = group.actionIDs && group.actionIDs.length > 0 
      ? group.actionIDs.map(id => parseInt(id, 10)).filter(id => !isNaN(id) && id > 0)
      : [];
    
    this.setState({
      activeFilters: newFilter,
      appliedFilterLabel: filterLabel,
      currentLevel: 'actions',
      breadcrumbs: [{
        label: filterLabel,
        groupBy,
        value: group.key,
        level: 'summary'
      }],
      filteredActionIDs: actionIDs
    });
    
    this.applyActionFilters(newFilter, actionIDs);
  }

  handleShowWorkOrders = (group, groupBy) => {
    // Show WO table when clicking "X WOs" link from any grouping
    // groupBy is the current grouping (e.g., 'office'), not 'work_order'
    const {dispatch} = this.props;
    const {activeFilters} = this.state;
    
    // When clicking "X WOs" from office view, we want to show WOs for that office
    // The group.key is the office name (e.g., 'NY'), and groupBy is 'office'
    // We need to pass this to getWorkOrderStatistics with the office filter
    const groupValue = group ? group.key : null;
    
    // Build breadcrumb label based on current grouping
    const breadcrumbLabel = group && groupBy 
      ? this.getBreadcrumbLabel(groupBy, groupValue)
      : 'Work Orders';
    
    this.setState({
      workOrdersLoading: true,
      currentLevel: 'workOrders',
      breadcrumbs: group && groupBy ? [{
        label: breadcrumbLabel,
        groupBy,
        value: groupValue,
        level: 'workOrders'  // Correct level for work orders view
      }] : [],
      selectedRowIndex: null
    });

    // Pass active filters to backend - backend will filter by office, problemType, etc.
    // When coming from office view, activeFilters should already have the office filter
    // But we also need to pass the current group's filter
    const filtersToUse = {...activeFilters};
    if (groupBy === 'office' && groupValue) {
      filtersToUse.office = groupValue;
    } else if (groupBy === 'problem_type' && groupValue) {
      filtersToUse.problemType = groupValue;
    } else if (groupBy === 'root_action' && groupValue) {
      filtersToUse.rootAction = groupValue;
    }
    
    const {createdOnFilter} = this.props;
    // When showing WOs from a specific group, we pass that group's filter
    // problemGroup should be the current grouping dimension, problemValue should be the group value
    dispatch(getWorkOrderStatistics(groupBy, groupValue, filtersToUse, createdOnFilter)).then(
      (response) => {
        if (response && response.workOrders) {
          this.setState({
            workOrders: response.workOrders,
            workOrdersLoading: false
          });
        } else {
          this.setState({workOrdersLoading: false});
        }
      }
    ).catch(() => {
      this.setState({workOrdersLoading: false});
    });
  }

  handleShowMuteModalForWO = (workOrder, actionIDs, problemCount, problemTypes) => {
    this.setState({
      showMuteModal: true,
      muteModalData: {
        actionIDs: actionIDs || [],
        problemCount: problemCount,
        workOrder: workOrder,
        problemTypes: problemTypes || []
      }
    });
  };

  handleCloseMuteModal = () => {
    this.setState({
      showMuteModal: false,
      muteModalData: null
    });
  };

  handleMuteSuccess = () => {
    this.handleCloseMuteModal();
    // Reload statistics to reflect muted problems
    this.loadStatistics();
  };

  handleShowActionsForWO = (wo, actionIDsArray, group, groupBy) => {
    // When clicking a WO row (when already in Work Order grouping)
    // The 'wo' parameter is the work order string (e.g., 'WO1225807')
    // actionIDsArray is the array of action IDs from the grouped work order data
    const actionIDs = actionIDsArray && actionIDsArray.length > 0
      ? actionIDsArray.map(id => parseInt(id, 10)).filter(id => !isNaN(id) && id > 0)
      : [];
    
    const newFilter = {
      ...this.state.activeFilters,
      workOrder: wo
    };
    
    const filterLabel = `Work Order = ${wo}`;
    
    this.setState({
      activeFilters: newFilter,
      appliedFilterLabel: filterLabel,
      currentLevel: 'actions',
      breadcrumbs: [
        ...this.state.breadcrumbs,
        {
          label: filterLabel,
          groupBy: 'work_order',
          value: wo,
          level: 'workOrders'
        }
      ],
      filteredActionIDs: actionIDs.length > 0 ? actionIDs : null
    });
    
    this.applyActionFilters(newFilter, actionIDs);
  }

  handleBreadcrumbClick = (breadcrumb) => {
    // Remove this breadcrumb and all after it
    const breadcrumbIndex = this.state.breadcrumbs.findIndex(b => 
      b.groupBy === breadcrumb.groupBy && b.value === breadcrumb.value
    );
    
    if (breadcrumbIndex >= 0) {
      const newBreadcrumbs = this.state.breadcrumbs.slice(0, breadcrumbIndex);
      const newFilters = {};
      
      // Rebuild filters from remaining breadcrumbs
      newBreadcrumbs.forEach(b => {
        if (b.groupBy === 'office') newFilters.office = b.value;
        else if (b.groupBy === 'problem_type') newFilters.problemType = b.value;
        else if (b.groupBy === 'root_action') newFilters.rootAction = b.value;
        else if (b.groupBy === 'work_order') newFilters.workOrder = b.value;
      });
      
      // Determine the correct level based on remaining breadcrumbs
      // The level is determined by what we're showing, not where we came from
      let newLevel = 'summary';
      if (newBreadcrumbs.length > 0) {
        // Check the last breadcrumb's level to determine where we're going
        const lastBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
        // If the breadcrumb's level is 'workOrders', we're showing work orders
        // Otherwise, if we have filters, we're showing actions
        if (lastBreadcrumb.level === 'workOrders') {
          newLevel = 'workOrders';
        } else {
          // Any other breadcrumb means we filtered and are showing actions
          newLevel = 'actions';
        }
      }
      
      // Clear filteredActionIDs when going back to summary or workOrders
      const shouldClearActionIDs = newLevel !== 'actions';
      
      // Notify parent if we're clearing action IDs
      if (shouldClearActionIDs) {
        const {onFilteredActionIDsChange} = this.props;
        if (onFilteredActionIDsChange) {
          onFilteredActionIDsChange(null);
        }
      }
      
      this.setState({
        breadcrumbs: newBreadcrumbs,
        activeFilters: newFilters,
        appliedFilterLabel: newBreadcrumbs.length > 0 ? newBreadcrumbs[newBreadcrumbs.length - 1].label : null,
        currentLevel: newLevel,
        selectedRowIndex: null,
        filteredActionIDs: shouldClearActionIDs ? null : this.state.filteredActionIDs,
        // If going back to workOrders level, we may need to reload work orders
        workOrders: newLevel === 'workOrders' ? [] : this.state.workOrders
      });
      
      // Reload data based on the new level
      if (newLevel === 'summary') {
        this.loadStatistics(this.state.groupBy, newFilters);
      } else if (newLevel === 'workOrders') {
        // Reload work orders with the current filters
        const lastBreadcrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
        if (lastBreadcrumb && lastBreadcrumb.level === 'workOrders') {
          // We're going back to workOrders level, reload the work orders
          const {dispatch} = this.props;
          const {createdOnFilter} = this.props;
          
          // Build filters: include filters from previous breadcrumbs (newFilters)
          // plus the filter from the workOrders breadcrumb itself
          const filtersToUse = {...newFilters};
          // Add the filter from the workOrders breadcrumb
          if (lastBreadcrumb.groupBy === 'office' && lastBreadcrumb.value) {
            filtersToUse.office = lastBreadcrumb.value;
          } else if (lastBreadcrumb.groupBy === 'problem_type' && lastBreadcrumb.value) {
            filtersToUse.problemType = lastBreadcrumb.value;
          } else if (lastBreadcrumb.groupBy === 'root_action' && lastBreadcrumb.value) {
            filtersToUse.rootAction = lastBreadcrumb.value;
          }
          
          this.setState({workOrdersLoading: true});
          // Use the groupBy and value from the breadcrumb to reload work orders
          dispatch(getWorkOrderStatistics(lastBreadcrumb.groupBy, lastBreadcrumb.value, filtersToUse, createdOnFilter)).then(
            (response) => {
              if (response && response.workOrders) {
                this.setState({
                  workOrders: response.workOrders,
                  workOrdersLoading: false
                });
              } else {
                this.setState({workOrdersLoading: false});
              }
            }
          ).catch(() => {
            this.setState({workOrdersLoading: false});
          });
        }
      }
      // If newLevel is 'actions', the actions are already filtered via filteredActionIDs
    }
  }
  
  handleClearFilter = () => {
    // Clear filtered action IDs in parent
    const {onFilteredActionIDsChange} = this.props;
    if (onFilteredActionIDsChange) {
      onFilteredActionIDsChange(null);
    }
    
    this.setState({
      activeFilters: {},
      appliedFilterLabel: null,
      breadcrumbs: [],
      currentLevel: 'summary',
      selectedRowIndex: null,
      filteredActionIDs: null
    });
    this.loadStatistics(this.state.groupBy, {});
  }

  getBreadcrumbLabel = (groupBy, value) => {
    const labels = {
      'problem_type': `Problem Type = ${this.formatGroupValue(groupBy, value)}`,
      'work_order': `Work Order = ${value}`,
      'office': `Office = ${value}`,
      'root_action': `Root Action = ${this.formatGroupValue(groupBy, value)}`
    };
    return labels[groupBy] || value;
  }

  formatGroupValue = (groupBy, value) => {
    // Replace "Unknown" and null values with better labels
    if (!value || value === 'Unknown' || value === 'NO_WO' || value === 'NO_ROOT' || value === 'UNCLASSIFIED') {
      if (groupBy === 'problem_type') {
        return 'Unclassified';
      } else if (groupBy === 'work_order') {
        return 'No Work Order';
      } else if (groupBy === 'root_action') {
        return 'No Root Action';
      } else if (groupBy === 'office') {
        return 'Unknown Office';
      }
      return 'Unassigned';
    }
    return value;
  }

  getGroupColumnHeader = (groupBy) => {
    const headers = {
      'problem_type': 'Problem Type',
      'work_order': 'Work Order',
      'office': 'Office',
      'root_action': 'Root Action'
    };
    return headers[groupBy] || 'Category';
  }

  getViewModeLabel = () => {
    const {groupBy, currentLevel} = this.state;
    if (currentLevel === 'actions') {
      const breadcrumb = this.state.breadcrumbs[this.state.breadcrumbs.length - 1];
      if (breadcrumb) {
        return `Viewing: ${this.getGroupColumnHeader(breadcrumb.groupBy)} → Actions`;
      }
      return `Viewing: ${this.getGroupColumnHeader(groupBy)} → Actions`;
    } else if (currentLevel === 'workOrders' && groupBy === 'work_order') {
      return 'Viewing: Work Orders → Actions';
    }
    return `Viewing: ${this.getGroupColumnHeader(groupBy)} → Actions`;
  }

  handleKeyDown = (e) => {
    // Only handle if dashboard is visible and focused
    if (!this.props.showProblematicOnly) return;
    
    const {currentLevel, selectedRowIndex} = this.state;
    
    if (currentLevel === 'summary') {
      // Arrow keys for row selection
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        // Handle row selection (would need to track row count)
      } else if (e.key === 'Enter' && selectedRowIndex !== null) {
        e.preventDefault();
        // Enter → WO list
        // This would need the selected row data
      } else if (e.key === 'Enter' && e.shiftKey && selectedRowIndex !== null) {
        e.preventDefault();
        // Shift+Enter → Actions
      }
    }
  }

  getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 0: return '#dc3545'; // 🔴 Overdue
      case 1: return '#fd7e14'; // 🟠 < 6h
      case 2: return '#ffc107'; // 🟡 < 24h
      case 3: return '#6c757d'; // ⚪ > 24h
      default: return '#6c757d';
    }
  }

  getUrgencyLabel = (urgency) => {
    switch (urgency) {
      case 0: return 'Overdue';
      case 1: return '< 6h';
      case 2: return '< 24h';
      case 3: return '> 24h';
      default: return 'Unknown';
    }
  }

  formatDueDate = (dueOn) => {
    if (!dueOn) return null;
    const date = new Date(dueOn);
    const now = new Date();
    const diffHours = (date - now) / (1000 * 60 * 60);
    
    if (diffHours < 0) {
      // Past date - show days + hours
      const totalHoursAgo = Math.abs(Math.floor(diffHours));
      const days = Math.floor(totalHoursAgo / 24);
      const hours = totalHoursAgo % 24;
      
      if (days > 0) {
        return `${days}d ${hours}h ago`;
      } else {
        return `${hours}h ago`;
      }
    } else if (diffHours < 24) {
      return `${Math.floor(diffHours)}h`;
    } else {
      const days = Math.floor(diffHours / 24);
      const hours = Math.floor(diffHours % 24);
      return `${days}d ${hours}h`;
    }
  }

  render() {
    const {showProblematicOnly} = this.props;
    const {
      statistics, summary, groupBy, loading,
      currentLevel, breadcrumbs, workOrders, workOrdersLoading
    } = this.state;

    if (!showProblematicOnly) {
      return null;
    }

    // Key rule: Show WO table when at workOrders level (regardless of groupBy)
    // This allows showing work orders filtered by office, problem type, etc.
    if (currentLevel === 'workOrders') {
      return this.renderWorkOrderView();
    } else if (currentLevel === 'actions') {
      // Actions are handled by the parent View component via filters
      return this.renderActionsBreadcrumb();
    }

    // Default: Summary view (aggregated table)
    return this.renderSummaryView();
  }

  renderSummaryView = () => {
    const {statistics, summary, groupBy, loading} = this.state;

    // Map groupBy to the actual field name returned by backend
    const fieldNameMap = {
      'problem_type': 'problemLabel',
      'work_order': 'workOrder',
      'root_action': 'rootActionType',
      'office': 'office'
    };
    const fieldName = fieldNameMap[groupBy] || groupBy;

    // Group statistics by the groupBy field
    const groupedStats = {};
    statistics.forEach(stat => {
      let key = stat[fieldName];
      // Normalize "Unknown" values to better labels
      if (!key || key === 'Unknown' || key === 'NO_WO' || key === 'NO_ROOT') {
        if (groupBy === 'work_order') {
          key = 'NO_WO';
        } else if (groupBy === 'root_action') {
          key = 'NO_ROOT';
        } else {
          key = 'UNCLASSIFIED';
        }
      }
      
      if (!groupedStats[key]) {
        groupedStats[key] = {
          total: 0,
          overdue: 0,
          imminent: 0,
          approaching: 0,
          actionIDs: new Set(),
          woMemfis: new Set(),
          earliestDueOn: null,
          latestDueOn: null
        };
      }
      const group = groupedStats[key];
      group.total += (stat.count || 0);
      group.overdue += (stat.overdueCount || 0);
      group.imminent += (stat.imminentCount || 0);
      group.approaching += (stat.approachingCount || 0);
      
      if (stat.actionIDs) {
        stat.actionIDs.split(',').forEach(id => group.actionIDs.add(id));
      }
      if (stat.woMemfisList) {
        stat.woMemfisList.split(',').forEach(wo => {
          if (wo && wo !== 'NO_WO') group.woMemfis.add(wo);
        });
      }
      
      if (stat.earliestDueOn && (!group.earliestDueOn || new Date(stat.earliestDueOn) < new Date(group.earliestDueOn))) {
        group.earliestDueOn = stat.earliestDueOn;
      }
      if (stat.latestDueOn && (!group.latestDueOn || new Date(stat.latestDueOn) > new Date(group.latestDueOn))) {
        group.latestDueOn = stat.latestDueOn;
      }
    });

    const sortedGroups = Object.entries(groupedStats)
      .map(([key, data]) => ({
        key,
        ...data,
        actionIDs: Array.from(data.actionIDs),
        woMemfis: Array.from(data.woMemfis)
      }))
      .sort((a, b) => {
        // Sort by urgency first (overdue > imminent > approaching > other)
        const urgencyA = a.overdue > 0 ? 0 : (a.imminent > 0 ? 1 : (a.approaching > 0 ? 2 : 3));
        const urgencyB = b.overdue > 0 ? 0 : (b.imminent > 0 ? 1 : (b.approaching > 0 ? 2 : 3));
        if (urgencyA !== urgencyB) return urgencyA - urgencyB;
        // Then by total count
        return b.total - a.total;
      });

    return (
      <DashboardContainer>
        {/* Breadcrumbs */}
        {this.state.breadcrumbs.length > 0 && this.renderBreadcrumbs()}
        
        {/* Top Summary Bar */}
        <SummaryBar>
          <SummaryItem color="#ffc107">
            <SummaryLabel>WAITING / STALLED</SummaryLabel>
            <SummaryValue>{summary.waitingStalledCount || 0}</SummaryValue>
          </SummaryItem>
          <SummaryItem color="#dc3545">
            <SummaryLabel>FAILED</SummaryLabel>
            <SummaryValue>{summary.failedCount || 0}</SummaryValue>
          </SummaryItem>
          <SummaryItem color="#fd7e14">
            <SummaryLabel>ACTIVE (OVERDUE)</SummaryLabel>
            <SummaryValue>{summary.activeOverdueCount || 0}</SummaryValue>
          </SummaryItem>
          <SummaryItem color="#17a2b8">
            <SummaryLabel>AT-RISK WOs</SummaryLabel>
            <SummaryValue>{summary.atRiskWOCount || 0}</SummaryValue>
          </SummaryItem>
          <SummaryItem color="#007bff">
            <SummaryLabel>TOTAL PROBLEMS</SummaryLabel>
            <SummaryValue>{summary.totalProblems || 0}</SummaryValue>
          </SummaryItem>
          {summary.maxRiskWO && (
            <SummaryItem color="#dc3545" highlight>
              <SummaryLabel>🔥 MAX RISK</SummaryLabel>
              <SummaryValue>{summary.maxRiskWO}</SummaryValue>
              <SummarySubtext>{summary.maxRiskWOCount} problems</SummarySubtext>
            </SummaryItem>
          )}
        </SummaryBar>

        {/* Category Selector */}
        <CategorySelector>
          <CategoryLabel>GROUP PROBLEMS BY:</CategoryLabel>
          <CategoryOptions>
            <CategoryOption 
              active={groupBy === 'problem_type'}
              onClick={() => this.handleGroupByChange('problem_type')}
            >
              Problem Type {groupBy === 'problem_type' && `(${sortedGroups.length} types)`}
            </CategoryOption>
            <CategoryOption 
              active={groupBy === 'work_order'}
              onClick={() => this.handleGroupByChange('work_order')}
            >
              Work Order {summary.atRiskWOCount > 0 && `(${summary.atRiskWOCount} at risk)`}
            </CategoryOption>
            <CategoryOption 
              active={groupBy === 'office'}
              onClick={() => this.handleGroupByChange('office')}
            >
              Office
            </CategoryOption>
            <CategoryOption 
              active={groupBy === 'root_action'}
              onClick={() => this.handleGroupByChange('root_action')}
            >
              Root Action
            </CategoryOption>
          </CategoryOptions>
        </CategorySelector>

        {/* View Mode Label and Active Filter */}
        <ViewModeContainer>
          <ViewModeContainer>
            <ViewModeLabel>{this.getViewModeLabel()}</ViewModeLabel>
            {this.state.appliedFilterLabel && (
              <ActiveFilter>
                Filter applied: {this.state.appliedFilterLabel}
                <FilterRemove onClick={this.handleClearFilter}>✕</FilterRemove>
              </ActiveFilter>
            )}
          </ViewModeContainer>
          {this.state.appliedFilterLabel && (
            <ActiveFilter>
              Filter applied: {this.state.appliedFilterLabel}
              <FilterRemove onClick={this.handleClearFilter}>✕</FilterRemove>
            </ActiveFilter>
          )}
        </ViewModeContainer>

        {/* Aggregated Table */}
        {loading ? (
          <LoadingMessage>Loading statistics...</LoadingMessage>
        ) : (
          <AggregatedTable>
            <TableHeader>
              <tr>
                <HeaderCell>{this.getGroupColumnHeader(groupBy)}</HeaderCell>
                <HeaderCell>Count</HeaderCell>
                <HeaderCell>Urgency</HeaderCell>
                <HeaderCell>Oldest</HeaderCell>
                <HeaderCell>{groupBy === 'work_order' ? 'Due Date' : 'WOs Affected'}</HeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {sortedGroups.map((group, idx) => {
                const urgency = group.overdue > 0 ? 0 : (group.imminent > 0 ? 1 : (group.approaching > 0 ? 2 : 3));
                const urgencyColor = this.getUrgencyColor(urgency);
                
                return (
                  <TableRow 
                    key={idx}
                    urgency={urgency}
                    selected={this.state.selectedRowIndex === idx}
                    onClick={(e) => this.handleRowClick(e, group)}
                    onMouseEnter={() => this.setState({selectedRowIndex: idx})}
                  >
                    <TableCell title={groupBy === 'work_order' ? 'Click to view actions for this work order' : 'Click to view actions'}>
                      <RowContent>
                        <ExpanderIcon>▸</ExpanderIcon>
                        {this.formatGroupValue(groupBy, group.key)}
                      </RowContent>
                    </TableCell>
                    <TableCell 
                      className="count-cell"
                      onClick={(e) => this.handleCountClick(e, group, groupBy)}
                      title={`View actions (${group.total})`}
                    >
                      <CountLink>{group.total}</CountLink>
                    </TableCell>
                    <TableCell>
                      <UrgencyBadge color={urgencyColor}>
                        {this.getUrgencyLabel(urgency)}
                      </UrgencyBadge>
                    </TableCell>
                    <TableCell>
                      {group.earliestDueOn ? (
                        <DueDateText urgency={urgency}>
                          {this.formatDueDate(group.earliestDueOn)}
                        </DueDateText>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {group.woMemfis.length > 0 ? (
                        <WOSummary>
                          {groupBy === 'work_order' ? (
                            // When grouping by WO, show due date
                            group.earliestDueOn ? (
                              <DueDateText urgency={urgency}>
                                {this.formatDueDate(group.earliestDueOn)}
                              </DueDateText>
                            ) : '-'
                          ) : (
                            // For other groupings, show WO count as clickable text
                            <WOCountLink 
                              className="wo-count-link"
                              onClick={(e) => this.handleWOCountClick(e, group, groupBy)}
                              title="Switch to Work Order grouping (preserves current filter)"
                            >
                              {group.woMemfis.length} {group.woMemfis.length === 1 ? 'WO' : 'WOs'}
                            </WOCountLink>
                          )}
                        </WOSummary>
                      ) : groupBy === 'work_order' ? '-' : 'No WO'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </AggregatedTable>
        )}
      </DashboardContainer>
    );
  }

  renderBreadcrumbs = () => {
    const {breadcrumbs} = this.state;
    
    return (
      <BreadcrumbContainer>
        {breadcrumbs.map((crumb, idx) => (
          <BreadcrumbItem key={idx}>
            {idx > 0 && <BreadcrumbSeparator>›</BreadcrumbSeparator>}
            <BreadcrumbLink 
              onClick={() => this.handleBreadcrumbClick(crumb)}
              removable={idx < breadcrumbs.length - 1}
            >
              {crumb.label}
              {idx < breadcrumbs.length - 1 && (
                <BreadcrumbRemove onClick={(e) => {
                  e.stopPropagation();
                  this.handleBreadcrumbClick(crumb);
                }}>✕</BreadcrumbRemove>
              )}
            </BreadcrumbLink>
          </BreadcrumbItem>
        ))}
      </BreadcrumbContainer>
    );
  }

  renderWorkOrderView = () => {
    const {workOrders, workOrdersLoading, breadcrumbs} = this.state;
    
    // Group WOs by urgency
    const groupedWOs = {};
    workOrders.forEach(wo => {
      const woNum = wo.workOrder;
      if (!groupedWOs[woNum]) {
        groupedWOs[woNum] = {
          workOrder: woNum,
          total: 0,
          overdue: 0,
          imminent: 0,
          approaching: 0,
          actionIDs: new Set(),
          problemTypes: new Set(),
          earliestDueOn: null,
          overdueSince: null
        };
      }
      const group = groupedWOs[woNum];
      group.total += (wo.count || 0);
      group.overdue += (wo.overdueCount || 0);
      group.imminent += (wo.imminentCount || 0);
      group.approaching += (wo.approachingCount || 0);
      
      if (wo.actionIDs) {
        wo.actionIDs.split(',').forEach(id => group.actionIDs.add(id));
      }
      if (wo.problemTypes) {
        wo.problemTypes.split(',').forEach(type => group.problemTypes.add(type));
      }
      
      if (wo.earliestDueOn && (!group.earliestDueOn || new Date(wo.earliestDueOn) < new Date(group.earliestDueOn))) {
        group.earliestDueOn = wo.earliestDueOn;
      }
      if (wo.overdueSince && (!group.overdueSince || new Date(wo.overdueSince) < new Date(group.overdueSince))) {
        group.overdueSince = wo.overdueSince;
      }
    });

    const sortedWOs = Object.values(groupedWOs)
      .map(wo => ({
        ...wo,
        actionIDs: Array.from(wo.actionIDs),
        problemTypes: Array.from(wo.problemTypes)
      }))
      .sort((a, b) => {
        const urgencyA = a.overdue > 0 ? 0 : (a.imminent > 0 ? 1 : (a.approaching > 0 ? 2 : 3));
        const urgencyB = b.overdue > 0 ? 0 : (b.imminent > 0 ? 1 : (b.approaching > 0 ? 2 : 3));
        if (urgencyA !== urgencyB) return urgencyA - urgencyB;
        return b.total - a.total;
      });

    return (
      <DashboardContainer>
        {this.renderBreadcrumbs()}
        
        <ViewTitle>WORK ORDERS ({sortedWOs.length} at risk)</ViewTitle>
        
        {workOrdersLoading ? (
          <LoadingMessage>Loading work orders...</LoadingMessage>
        ) : (
          <AggregatedTable>
            <TableHeader>
              <HeaderCell>WO</HeaderCell>
              <HeaderCell>Due</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Problems</HeaderCell>
              <HeaderCell>Max Severity</HeaderCell>
            </TableHeader>
            <TableBody>
              {sortedWOs.map((wo, idx) => {
                const urgency = wo.overdue > 0 ? 0 : (wo.imminent > 0 ? 1 : (wo.approaching > 0 ? 2 : 3));
                const urgencyColor = this.getUrgencyColor(urgency);
                const summaryBreadcrumb = breadcrumbs[0];
                
                return (
                  <TableRow 
                    key={idx}
                    urgency={urgency}
                    onClick={() => this.handleShowActionsForWO(wo.workOrder, wo.actionIDs, {key: summaryBreadcrumb ? summaryBreadcrumb.value : null}, summaryBreadcrumb ? summaryBreadcrumb.groupBy : null)}
                  >
                    <TableCell>
                      <WOTag clickable>{wo.workOrder}</WOTag>
                    </TableCell>
                    <TableCell>
                      {wo.earliestDueOn ? (
                        <DueDateText urgency={urgency}>
                          {this.formatDueDate(wo.earliestDueOn)}
                          {wo.overdue > 0 && wo.overdueSince && (
                            <OverdueText> (overdue)</OverdueText>
                          )}
                        </DueDateText>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <UrgencyBadge color={urgencyColor}>
                        {this.getUrgencyLabel(urgency)}
                      </UrgencyBadge>
                    </TableCell>
                    <TableCell>{wo.total}</TableCell>
                    <TableCell>
                      {wo.problemTypes.length > 0 ? (
                        <MaxSeverityLabel
                          onClick={(e) => {
                            e.stopPropagation();
                            if (wo.total > 1) {
                              this.handleShowMuteModalForWO(wo.workOrder, wo.actionIDs, wo.total, wo.problemTypes);
                            }
                          }}
                        >
                          {wo.problemTypes.join(', ')}
                        </MaxSeverityLabel>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </AggregatedTable>
        )}
        {this.state.showMuteModal && this.state.muteModalData && (
          <IgnoreProblemModal
            actionIDs={this.state.muteModalData.actionIDs}
            problemCount={this.state.muteModalData.problemCount}
            workOrder={this.state.muteModalData.workOrder}
            problemTypes={this.state.muteModalData.problemTypes}
            onClose={this.handleCloseMuteModal}
            onIgnore={this.handleMuteSuccess}
            user={this.props.user}
          />
        )}
      </DashboardContainer>
    );
  }

  renderActionsBreadcrumb = () => {
    return (
      <DashboardContainer>
        {this.renderBreadcrumbs()}
        <InfoMessage>
          Actions filtered below. Use breadcrumbs to navigate back.
        </InfoMessage>
      </DashboardContainer>
    );
  }
}

const DashboardContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
`;

const SummaryBar = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SummaryItem = styled.div`
  flex: 1;
  min-width: 120px;
  background: white;
  border-left: 4px solid ${props => props.color};
  padding: 10px 15px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  ${props => props.highlight && `
    background: #fff3cd;
    border-left-width: 6px;
    font-weight: bold;
  `}
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  color: #6c757d;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #212529;
`;

const SummarySubtext = styled.div`
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
`;

const CategorySelector = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background: white;
  border-radius: 4px;
`;

const CategoryLabel = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: #495057;
  margin-bottom: 8px;
`;

const CategoryOptions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const CategoryOption = styled.button`
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#495057'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#0056b3' : '#f8f9fa'};
    border-color: ${props => props.active ? '#0056b3' : '#adb5bd'};
  }
`;

const AggregatedTable = styled.table`
  width: 100%;
  background: white;
  border-collapse: collapse;
  border-radius: 4px;
  overflow: hidden;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const HeaderCell = styled.th`
  padding: 12px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  color: #495057;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid #dee2e6;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  ${props => props.selected && `
    background-color: #e7f3ff !important;
    border-left: 3px solid #007bff;
  `}
  
  ${props => props.urgency === 0 && `
    border-left: 3px solid #dc3545;
  `}
  
  ${props => props.urgency === 1 && `
    border-left: 3px solid #fd7e14;
  `}
`;

const TableCell = styled.td`
  padding: 12px;
  font-size: 13px;
  color: #212529;
`;

const UrgencyBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  background-color: ${props => props.color};
  color: white;
  font-size: 11px;
  font-weight: 600;
`;

const DueDateText = styled.span`
  color: ${props => {
    if (props.urgency === 0) return '#dc3545';
    if (props.urgency === 1) return '#fd7e14';
    if (props.urgency === 2) return '#ffc107';
    return '#6c757d';
  }};
  font-weight: ${props => props.urgency <= 1 ? 'bold' : 'normal'};
`;

const WOTags = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const WOTag = styled.span`
  padding: 2px 6px;
  background: #e9ecef;
  border-radius: 3px;
  font-size: 11px;
  color: #495057;
  ${props => props.clickable && `
    cursor: pointer;
    &:hover {
      background: #dee2e6;
    }
  `}
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: white;
  border-radius: 4px;
  margin-bottom: 15px;
  font-size: 13px;
`;

const BreadcrumbItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbSeparator = styled.span`
  color: #6c757d;
`;

const BreadcrumbLink = styled.span`
  color: ${props => props.removable ? '#007bff' : '#495057'};
  cursor: ${props => props.removable ? 'pointer' : 'default'};
  padding: 4px 8px;
  border-radius: 3px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    ${props => props.removable && `
      background: #f8f9fa;
      color: #0056b3;
    `}
  }
`;

const BreadcrumbRemove = styled.span`
  color: #6c757d;
  font-size: 12px;
  margin-left: 4px;
  
  &:hover {
    color: #dc3545;
  }
`;

const ViewTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 15px;
`;

const OverdueText = styled.span`
  color: #dc3545;
  font-weight: bold;
`;

const InfoMessage = styled.div`
  padding: 15px;
  background: #e7f3ff;
  border-left: 4px solid #007bff;
  border-radius: 4px;
  color: #004085;
`;

const ViewModeContainer = styled.div`
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ViewModeLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  font-weight: 600;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const ActiveFilter = styled.div`
  font-size: 12px;
  color: #004085;
  background: #cce5ff;
  border-left: 4px solid #007bff;
  padding: 8px 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const FilterRemove = styled.span`
  cursor: pointer;
  color: #004085;
  font-weight: bold;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 3px;
  
  &:hover {
    background: #007bff;
    color: white;
  }
`;

const WOSummary = styled.div`
  display: flex;
  align-items: center;
`;

const WOCountLink = styled.span`
  cursor: pointer;
  color: #007bff;
  text-decoration: underline;
  font-weight: 600;
  
  &:hover {
    color: #0056b3;
  }
`;

const MaxSeverityLabel = styled.span`
  background-color: rgb(255, 0, 0);
  color: #fff;
  padding: 2px 4px;
  border-radius: 3px;
  display: inline-block;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  }
`;

const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #6c757d;
`;

const RowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ExpanderIcon = styled.span`
  color: #6c757d;
  font-size: 12px;
  transition: transform 0.2s;
`;

const CountLink = styled.span`
  cursor: pointer;
  color: #007bff;
  font-weight: 600;
  text-decoration: underline;
  
  &:hover {
    color: #0056b3;
  }
`;

ProblemDashboard.propTypes = {
  showProblematicOnly: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func,
  createdOnFilter: PropTypes.object, // {start: Date, end: Date}
  onFilteredActionIDsChange: PropTypes.func // Callback to notify parent of filtered action IDs
};

export default ProblemDashboard;

